import * as React from "react";
import {useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import Modal from "../Modal";
import {useTranslation} from "react-i18next";
import {isCart, useUnsafeCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import {useMediaQuery} from "react-responsive";
import styles from "./BuildYourOwnString.module.scss";
import modalStyles from "../Modal.module.scss";
import {isoLocales} from "../../payments/PaymentController";
import {getShopId} from "../../getShopId";
import {Button, ButtonType} from "../../form/Button/Button";
import {updateUrlParams} from "../../../cart/CartIcon/CartIconPortal";
import {cdnDomain} from "../../../../helpers/cnxDomains";

type StringEventType = "byoLoaded" | "cartUpdate" | "designUpdate";

type StringItemType = {
  ean: string;
  quantity: number;
  article: string;
};

type StringDataType = {
  designid: string;
  items: StringItemType[];
};

type StringUpdateType = {
  event: StringEventType;
  data: StringDataType;
};

const guid = "sit_MzEyMDUyODAwMA";
const country = isoLocales[getShopId()].locale.split("-")[1];
const lang = () => {
  const lan = document.body.dataset.lang || "";

  return ["de", "fr"].includes(lan) ? lan : "en";
};

export const stringConfigParam = "build-your-own-string-shelf";

const urlParams = new URLSearchParams(window.location.search);
const viewParamStringConfig = urlParams.get("view") === stringConfigParam;

const BuildYourOwnString: React.FC = () => {
  const context = useUnsafeCartContext();
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const {t} = useTranslation();
  const isMobile = useMediaQuery({maxWidth: 876});

  const adviceLink = document.body.getElementsByClassName("build-your-own-string")[0] as HTMLElement | undefined;

  useEffect(() => {
    const messageHandler = (event: MessageEvent<StringUpdateType>) => {
      if (event.data.event === "byoLoaded" && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({event: "enableCart", data: {}}, "*");
      }

      if (event.data.event === "cartUpdate") {
        // eslint-disable-next-line no-console
        if (!context) {
          return;
        }

        const workingCart = async () => {
          return isCart(context) ? {cart: context.cart, done: () => null} : await context.requestCart();
        };

        void workingCart().then(({cart, done}) => {
          const stringItems = event.data.data.items.map((item) => ({
            itemId: null,
            gtin: item.ean,
            quantity: item.quantity,
          }));
          void cart.addMultipleItems(stringItems).then(() => {
            context.setItemsAdded(stringItems);
            done();
          });
        });

        closeFunction();
        updateUrlParams(null, "replace");
      }

      // if (event.data.event === "designUpdate") {
      //   eslint-disable-next-line no-console
      //   console.log("yeah designUpdate!!");
      // }
    };

    if (viewParamStringConfig) {
      setIsVisible(true);
    }

    if (adviceLink || viewParamStringConfig) {
      adviceLink?.classList.add("visible");
      window.addEventListener("message", messageHandler, true);
    }

    return () => window.removeEventListener("message", messageHandler);
  }, []);

  useEffect(() => {
    if (viewParamStringConfig) {
      setIsVisible(true);
    }
  }, [urlParams]);

  // browser back button function is supposed to just close the modal
  useEffect(() => {
    if (isVisible && !viewParamStringConfig) {
      window.history.pushState(isVisible, window.location.pathname, "");
      window.addEventListener("popstate", closeModal);
    }

    return () => window.removeEventListener("popstate", closeModal);
  }, [isVisible]);

  if (!adviceLink && !viewParamStringConfig) {
    return null;
  }

  const openFunction = () => {
    setIsVisible(true);
  };

  const closeModal = () => {
    setIsVisible(false);
    const anyModalSection = document.querySelector("#ModalRootElement > section");
    document.body.style.overflow = !!anyModalSection ? "visible" : "";
  };

  const closeFunction = () => {
    closeModal();

    if (!viewParamStringConfig) {
      window.history.back();
    }
  };

  const BuildYourOwnStringModal: React.FC = () => {
    return (
      <Modal fullScreen={true} isVisible={true}>
        <div className={styles.buildYourOwnString}>
          {/* Weird substitute for generic modal close mechanism. */}
          {/* Necessary to brute force place PA _below_ the iframe, not above */}
          <div className={`${modalStyles.closeButton} ${styles.closeButtonModifier}`} onClick={closeFunction}>
            <i className="icon icon-close icon-size--22" />
          </div>
          <iframe
            ref={iframeRef}
            width="100%"
            height="100%"
            src={`https://build-your-own.stringfurniture.com/?lan=${lang()}&countrycode=${country}&guid=${guid}`}
            allowFullScreen
            scrolling="no"
          />
        </div>
      </Modal>
    );
  };

  if (!adviceLink) {
    return <>{isVisible && !isMobile && <BuildYourOwnStringModal />}</>;
  }

  // url to image, without file extension!
  const picturePath = `${cdnDomain[getShopId()]}/media/_Banner_Sonstiges/string-config-banner`;

  return ReactDOM.createPortal(
    <React.Fragment>
      {isMobile ? (
        <a href={adviceLink.dataset.link}>{adviceLink.dataset.text}</a>
      ) : (
        <div className={styles.stringConfigLink} onClick={openFunction} title={t("product.createCustomConfig")}>
          <div className={styles.stringConfigLinkImg}>
            <picture>
              <source type="image/webp" srcSet={`${picturePath}.webp 1x, ${picturePath}@2x.webp 2x`} />
              <source type="image/jpg" srcSet={`${picturePath}.jpg 1x, ${picturePath}@2x.jpg 2x`} />
              <img
                src={`${picturePath}.jpg`}
                alt={t("product.customStringShelveConfig.title")}
                loading="lazy"
                width="385"
                height="182"
              />
            </picture>
          </div>
          <div className={styles.stringConfigLinkContent}>
            <h3 className={styles.stringConfigLinkContentTitle}>{t("product.customStringShelveConfig.title")}</h3>
            <p>{t("product.customStringShelveConfig.text")}</p>
            <Button type={ButtonType.outlined}>{t("product.customStringShelveConfig.button")}</Button>
          </div>
        </div>
      )}
      {isVisible && <BuildYourOwnStringModal />}
    </React.Fragment>,
    adviceLink,
  );
};

export default BuildYourOwnString;
